/**
 * Communication test — simplified (10 scenarios), four options each.
 * scoreTable values are teamwork/collaboration weights (0–100) per option.
 */

const L = (en, tc, sc) => ({
  en,
  tc: tc != null ? tc : en,
  sc: sc != null ? sc : en
});

const PROMPT = L('What would you do?', '你會怎麼做？', '你会怎么做？');

/**
 * @param {{en:string,tc:string,sc:string}} scenario
 * @param {[{en:string,tc:string,sc:string},{en:string,tc:string,sc:string},{en:string,tc:string,sc:string},{en:string,tc:string,sc:string}]} options
 * @param {[number, number, number, number]} scores
 */
function item(scenario, options, scores) {
  return { scenario, options, scores };
}

/** @type {ReturnType<typeof item>[]} */
const COMM_ITEMS = [
  item(
    L(
      'You have been working closely with your manager on a critical business-wide process improvement project. The project has received a lot of interest due to the simplicity it will create for staff when analysing data. The project has been going well and is due to launch in two days. You and your manager have planned to demonstrate the new process to the rest of the business on the day of the launch. However, your manager has received an urgent request to attend a client meeting that could be highly valuable for the business. Your manager has contacted another manager who is available on the day to step in to support you but nothing has been confirmed yet. You are not sure what to do.',
      '你一直與上司緊密合作，推行一個重要、影響全公司的流程改善計劃，能令同事分析數據時更簡便，因此備受關注。計劃進展順利，兩天後就要正式推出，你和上司原定在推出當天向公司其他人示範新流程。但上司臨時要出席一個可能為公司帶來重要價值的客戶會議，已聯絡另一位當天有空的經理支援你，但仍未確認。你不確定該怎樣做。',
      '你一直与上司紧密合作，推进一个重要、影响全公司的流程改善项目，可让同事在分析数据时更简便，因此备受关注。项目进展顺利，两天后就要正式上线，你和上司原定在上线当天向公司其他人示范新流程。但上司临时要出席一个可能为公司带来重要价值的客户会议，已联系另一位当天有空的经理支援你，但仍未确认。你不确定该怎么做。'
    ),
    [
      L(
        'Wait for the other manager to confirm that they can support first before preparing for the launch.',
        '等另一位經理確認可以支援後，才開始為推出作準備。',
        '等另一位经理确认可以支援后，才开始为上线作准备。'
      ),
      L(
        'Write an e-mail to the other manager highlighting that you are waiting for their response before you can move forward with preparing for the launch.',
        '發電郵給該經理，表示你正在等他的回覆，否則無法繼續準備。',
        '发邮件给该经理，表示你正在等他的回复，否则无法继续准备。'
      ),
      L(
        'Discuss with your manager a list of other people you could involve to support the launch in case the manager that has been contacted can’t support.',
        '與上司討論一份備選支援名單，以防被聯絡的經理無法支援。',
        '与上司讨论一份备选支援名单，以防被联系的经理无法支援。'
      ),
      L(
        'Take charge to arrange a meeting with the other manager directly and start to prepare for the demonstration process.',
        '主動安排與該經理開會對接，並開始準備示範流程。',
        '主动安排与该经理开会对接，并开始准备示范流程。'
      )
    ],
    [40, 30, 90, 60]
  ),
  item(
    L(
      'You have been in your role for 6 months and today you are receiving feedback from your manager on your performance in your performance review meeting. Over this period, you have learned a lot and you feel that you have performed well in your assigned tasks and projects. In your meeting, your manager expresses that she is pleased with your performance so far but feels that you could improve your presentation skills to make it more engaging. You are slightly surprised as you thought your presentations were always well received by your audience. You were planning on applying your same approach to an upcoming presentation.',
      '你入職 6 個月，今天在績效評核會議中獲得上司回饋。這段時間你學到很多，也覺得自己在任務與項目上表現不錯。上司表示對你目前表現滿意，但認為你的匯報技巧可更吸引。你有點意外，因為你一直以為觀眾反應良好，而且你原本打算在即將到來的匯報沿用同一做法。',
      '你入职 6 个月，今天在绩效评核会议中获得上司反馈。这段时间你学到很多，也觉得自己在任务与项目上表现不错。上司表示对你目前表现满意，但认为你的汇报技巧可更吸引。你有点意外，因为你一直以为观众反应良好，而且你原本打算在即将到来的汇报沿用同一做法。'
    ),
    [
      L(
        'Speak to individuals who have attended your presentations in the past and gather their feedback as you are concerned they may have not been as engaged as you thought.',
        '找曾聽過你匯報的人收集回饋，因為你擔心他們其實未如你所想般投入。',
        '找曾听过你汇报的人收集反馈，因为你担心他们其实未如你所想般投入。'
      ),
      L(
        'Focus on finishing your preparations for your upcoming presentations but make a note of the feedback and schedule in some to think about how you could incorporate it in for future presentations.',
        '先專注完成即將到來的匯報準備，但記下回饋，日後再安排時間思考如何在未來改進。',
        '先专注完成即将到来的汇报准备，但记下反馈，日后再安排时间思考如何在未来改进。'
      ),
      L(
        'Take your manager’s feedback on board and consider what you could do to make it more engaging as you’re positive that there is room for improvement.',
        '接受上司回饋並思考如何令匯報更吸引，你相信自己仍有改善空間。',
        '接受上司反馈并思考如何令汇报更吸引，你相信自己仍有改善空间。'
      ),
      L(
        'Make a note of the feedback and suggest a time to discuss it in more detail with your manager so that you can better understand.',
        '記下回饋，並建議約時間與上司深入討論，以更清楚理解改進方向。',
        '记下反馈，并建议约时间与上司深入讨论，以更清楚理解改进方向。'
      )
    ],
    [80, 20, 40, 70]
  ),
  item(
    L(
      'You produce reports to update the status of each client project that you are involved in. Every Thursday you update all of the reports so that team managers can refer to it in their Friday board meetings. Your manager has reviewed your status updates and has mentioned that some members of the management team may require more levels of detail. While you see the value of this, you feel that it may take much longer and you would rather spend your time in client meetings than writing lengthier status updates.',
      '你需要為所參與的每個客戶項目撰寫進度報告。每星期四你都會更新所有報告，讓團隊經理在星期五的會議上參考。上司看過你的進度更新後指出，管理層部分人士可能需要更詳細的內容。你明白其價值，但擔心會耗費更多時間，你更希望把時間用在客戶會議而不是寫更長的更新。',
      '你需要为所参与的每个客户项目撰写进度报告。每周四你都会更新所有报告，让团队经理在周五的会议上参考。上司看过你的进度更新后指出，管理层部分人士可能需要更详细的内容。你明白其价值，但担心会耗费更多时间，你更希望把时间用在客户会议而不是写更长的更新。'
    ),
    [
      L(
        'Take up the challenge and make sure that you include additional detail where possible in your status updates by the Thursday deadline.',
        '接受挑戰，在星期四限期前盡量在更新中加入更多細節。',
        '接受挑战，在周四期限前尽量在更新中加入更多细节。'
      ),
      L(
        'Tell your manager that you will try to take the feedback on board but will be able to dedicate more time to the status updates during less busier periods.',
        '告訴上司你會嘗試採納回饋，但只能在較不忙的時段才可投入更多時間寫進度更新。',
        '告诉上司你会尝试采纳反馈，但只能在较不忙的时段才可投入更多时间写进度更新。'
      ),
      L(
        'Try to vary the amount of detail in your updates so that you can show that you have acknowledged your manager’s feedback but avoid spending too much time so that you can focus on your client meetings.',
        '嘗試在更新中調整不同詳盡程度，表面上回應上司意見，但避免花太多時間，以便專注客戶會議。',
        '尝试在更新中调整不同详尽程度，表面上回应上司意见，但避免花太多时间，以便专注客户会议。'
      ),
      L(
        'Schedule in a meeting with your manager to discuss their perspective on the ideal amount of information needed.',
        '安排與上司會議，討論理想的資訊詳盡程度與期望。',
        '安排与上司会议，讨论理想的信息详尽程度与期望。'
      )
    ],
    [30, 40, 20, 80]
  ),
  item(
    L(
      'A colleague of yours produced a report for a manager in another team just before he went on annual leave. The manager has contacted you in his absence highlighting a number of areas that he needs clarity on as he does not fully understand the conclusions he has drawn from the data provided in the report. The manager needs this information to be able to complete an upcoming presentation and has made it clear that he is not satisfied. As your colleague is out of the office for the rest of the week, the manager is expecting you to help resolve the issues highlighted. However, you already have a few of your own reports to complete for another manager today.',
      '同事在放年假前為另一個團隊的經理完成了一份報告。該經理在同事缺席期間聯絡你，指出他對報告中數據得出的結論有多處不明白，並表示不滿。他需要釐清內容才能完成即將到來的匯報。由於同事整個星期都不在，該經理期望你協助解決問題。但你今天也要為另一位經理完成自己幾份報告。',
      '同事在放年假前为另一个团队的经理完成了一份报告。该经理在同事缺席期间联系你，指出他对报告中数据得出的结论有多处不明白，并表示不满。他需要厘清内容才能完成即将到来的汇报。由于同事整周都不在，该经理期望你协助解决问题。但你今天也要为另一位经理完成自己几份报告。'
    ),
    [
      L(
        'Take out time to actively review your colleague’s report and talk through conclusions drawn with the manager.',
        '抽時間仔細檢視同事報告，並與該經理逐點講解結論。',
        '抽时间仔细检查同事报告，并与该经理逐点讲解结论。'
      ),
      L(
        'Read your colleague’s report and e-mail the manager to explain the conclusions at a high level so that you help him but can focus on completing the rest of your reports.',
        '閱讀報告後以電郵作高層次解釋，好讓你提供協助但仍可專注完成自己的報告。',
        '阅读报告后以邮件作高层次解释，好让你提供协助但仍可专注完成自己的报告。'
      ),
      L(
        'Read your colleague’s report and e-mail the manager to arrange a convenient time to explain the conclusions drawn.',
        '閱讀報告後電郵該經理，安排合適時間再作講解。',
        '阅读报告后邮件该经理，安排合适时间再作讲解。'
      ),
      L(
        'Explain to the manager that you are happy to help him after you have completed your own reports for the other manager.',
        '向該經理表示你願意協助，但要先完成今天交給另一位經理的報告。',
        '向该经理表示你愿意协助，但要先完成今天交给另一位经理的报告。'
      )
    ],
    [80, 40, 70, 50]
  ),
  item(
    L(
      'You have been collaborating with a senior colleague to create an investment strategy for an important client. You and your colleague are due to share your recommendations in a meeting later this afternoon. As your colleague has successfully worked with the client in the past and you have not worked with this client before, you agree that the best approach would be for your colleague to take the lead on presenting the strategy, with you supporting. As the meeting approaches and you arrive at the client’s office, you receive an e-mail from your colleague saying that she is running late by 15 minutes as her previous meeting overran. While you are reading her e-mail, the client greets you.',
      '你與一位資深同事合作，為重要客戶制定投資策略，你們今天下午要在會議中分享建議。由於同事過去成功與該客戶合作，而你未曾接觸這位客戶，你們同意由同事主講、你作支援。臨近會議時你到達客戶辦公室，收到同事電郵說她上一個會議拖延，會遲到 15 分鐘。你正在讀電郵時，客戶已向你打招呼。',
      '你与一位资深同事合作，为重要客户制定投资策略，你们今天下午要在会议中分享建议。由于同事过去成功与该客户合作，而你未曾接触这位客户，你们同意由同事主讲、你作支援。临近会议时你到达客户办公室，收到同事邮件说她上一场会议拖延，会迟到 15 分钟。你正在读邮件时，客户已向你打招呼。'
    ),
    [
      L(
        'Greet the client and e-mail your colleague back to let her know that you and the client are waiting for her so that you can start the meeting.',
        '向客戶致意，並回電郵告訴同事你和客戶正在等她，等她到場才開始會議。',
        '向客户致意，并回邮件告诉同事你和客户正在等她，等她到场才开始会议。'
      ),
      L(
        'Greet the client and explain that you need to call your colleague to let her know that they have arrived before you can start the meeting.',
        '向客戶致意，並解釋你需要先致電同事告知已到場，之後才能開始會議。',
        '向客户致意，并解释你需要先致电同事告知已到场，之后才能开始会议。'
      ),
      L(
        'Use the opportunity to introduce yourself and talk to the client about the agenda until your colleague arrives.',
        '趁機自我介紹，並先與客戶簡述議程與重點，直到同事到達。',
        '趁机自我介绍，并先与客户简述议程与重点，直到同事到达。'
      ),
      L(
        'Greet the client and tell them that you need to find your colleague and will return shortly.',
        '向客戶致意後表示你要去找同事，稍後再回來。',
        '向客户致意后表示你要去找同事，稍后再回来。'
      )
    ],
    [30, 40, 80, 10]
  ),
  item(
    L(
      'Your role requires you to regularly work with consumer data and identify market trends to make recommendations for your clients. Your organisation has recently introduced a set of new guidelines in relation to the way consumer data should be stored and discarded. As you have been successfully implementing the new guidelines, your manager has asked you to do a demonstration and explain the benefits of this new approach to the graduate marketing team who very regularly use and depend on consumer data. You have come across the contact details of various individuals in the marketing team, however you have not officially met with anyone from the team and are unsure of the best approach to get in touch.',
      '你的工作需要經常處理消費者數據並分析市場趨勢，為客戶提供建議。公司最近推出新指引，規範消費者數據的存放與銷毀方式。你成功落實新指引後，上司要求你向常用並依賴消費者數據的畢業生市場團隊作示範並解釋新做法的好處。你找到市場團隊多位同事的聯絡資料，但你未正式認識他們，不確定應如何聯絡最合適。',
      '你的工作需要经常处理消费者数据并分析市场趋势，为客户提供建议。公司最近推出新指引，规范消费者数据的存放与销毁方式。你成功落实新指引后，上司要求你向常用并依赖消费者数据的毕业生市场团队作示范并解释新做法的好处。你找到市场团队多位同事的联系方式，但你未正式认识他们，不确定应如何联系最合适。'
    ),
    [
      L(
        'Hold back from reaching out to anyone directly and send your manager the list of the contact details asking for their recommendation on who you should contact.',
        '先不直接聯絡任何人，把聯絡名單交給上司，請他建議應該找誰。',
        '先不直接联系任何人，把联系名单交给上司，请他建议应该找谁。'
      ),
      L(
        'Visit the marketing department and approach one of the managers to schedule in a time to discuss this opportunity.',
        '到市場部親身拜訪，找一位經理安排時間討論這次示範。',
        '到市场部当面拜访，找一位经理安排时间讨论这次示范。'
      ),
      L(
        'Send out an e-mail introducing yourself to the wider marketing distribution group list and wait for a response before taking up the opportunity any further.',
        '向市場部的群組郵件名單發介紹電郵，並等待回覆後再進一步跟進。',
        '向市场部的群组邮件名单发介绍邮件，并等待回复后再进一步跟进。'
      ),
      L(
        'Send out an e-mail to specific individuals from the marketing team using the contact details that you have and suggest some suitable dates for the demonstration.',
        '根據手上的聯絡資料，直接電郵給幾位相關同事，並提出幾個合適的示範日期。',
        '根据手上的联系方式，直接邮件给几位相关同事，并提出几个合适的示范日期。'
      )
    ],
    [50, 80, 40, 60]
  ),
  item(
    L(
      'A member of your team approaches you and asks for your input on a proposal that she is trying to quickly put together in response to an unexpected business opportunity with a major client. As she is in back-to-back meetings with clients all day, she has not had the time to detail exactly what she needs to complete the proposal. However, she has sent you her draft proposal with a brief note stating that she needs to finalise one solution that could be recommended to the prospective client before she can send off her proposal. There are a number of possible options contained within her draft proposal but you are unsure where to start.',
      '團隊成員請你就一份提案提供意見，她正要趕在短時間內回應一個大型客戶的突發商機。她整天都在連續開會，沒時間清楚說明需要你協助哪些部分，只把提案草稿寄給你，並簡短寫道：在提交前她必須定案一個可向潛在客戶推薦的方案。草稿中有多個可行選項，你不確定應從哪裡開始。',
      '团队成员请你就一份提案提供意见，她正要赶在短时间内回应一个大型客户的突发商机。她整天都在连续开会，没时间清楚说明需要你协助哪些部分，只把提案草稿发给你，并简短写道：在提交前她必须定案一个可向潜在客户推荐的方案。草稿中有多个可行选项，你不确定应从哪里开始。'
    ),
    [
      L(
        'Critically analyse each of the solutions and focus on the pros and cons until you can make a strong recommendation.',
        '逐一仔細分析每個方案的利弊，直到你能提出一個很有力的建議。',
        '逐一仔细分析每个方案的利弊，直到你能提出一个很有力的建议。'
      ),
      L(
        'Focus your time on picking out the most obvious cons for each solution and make a couple of recommendations based on solutions that look the most plausible.',
        '集中指出每個方案最明顯的缺點，並就看起來較可行的方案提出幾個建議。',
        '集中指出每个方案最明显的缺点，并就看起来较可行的方案提出几个建议。'
      ),
      L(
        'Complete a quick review due to the limited amount of time and suggest the benefits of why your colleague should consider one of the solutions.',
        '考慮到時間有限，快速審閱並提出其中一個方案的好處，讓同事可直接考慮採用。',
        '考虑到时间有限，快速审阅并提出其中一个方案的好处，让同事可直接考虑采用。'
      ),
      L(
        'Focus your time on picking out the most popular solutions amongst clients with similar challenges.',
        '把時間用來找出過去面對類似挑戰的客戶最常採用的方案。',
        '把时间用来找出过去面对类似挑战的客户最常采用的方案。'
      )
    ],
    [30, 40, 70, 20]
  ),
  item(
    L(
      'You are working as part of a large team on a complex client project with multiple phases. As the first phase completes you begin to prepare for phase two. In the process of doing this, you notice that the project plan does not align with the breakdown of the payment schedule that was sent to the client when the project was sold. You are contemplating on whether you should check all the project stages in line with the payment schedule to ensure that client doesn’t miss any payments, but you are somewhat reluctant as you know it is not your responsibility.',
      '你在一個大型團隊中參與多階段的複雜客戶項目。第一階段完成後，你開始準備第二階段，並發現項目計劃與當初售賣項目時發給客戶的付款時間表不一致。你在考慮是否應把所有項目階段逐一對照付款時間表，確保客戶不會漏付款，但你有點猶豫，因為你知道這並非你的職責。',
      '你在一个大型团队中参与多阶段的复杂客户项目。第一阶段完成后，你开始准备第二阶段，并发现项目计划与当初售卖项目时发给客户的付款时间表不一致。你在考虑是否应把所有项目阶段逐一对照付款时间表，确保客户不会漏付款，但你有点犹豫，因为你知道这并非你的职责。'
    ),
    [
      L(
        'Tell the project manager that you think you have found a discrepancy between the project plan and the payment schedule and they should look into it.',
        '告訴項目經理你認為計劃與付款表有出入，並請他處理。',
        '告诉项目经理你认为计划与付款表有出入，并请他处理。'
      ),
      L(
        'Make a note of the assumed discrepancies and contact the project manager to arrange a meeting to go through it with them at a later date.',
        '先記錄你認為的出入之處，並聯絡項目經理安排之後開會逐項對照。',
        '先记录你认为的出入之处，并联系项目经理安排之后开会逐项对照。'
      ),
      L(
        'Ask the project manager if the project plan is the most up to date version before checking if all of the project stages are in line with the costs displayed on the payment schedule.',
        '先向項目經理確認項目計劃是否為最新版本，再決定是否把各階段與付款表逐一核對。',
        '先向项目经理确认项目计划是否为最新版本，再决定是否把各阶段与付款表逐一核对。'
      ),
      L(
        'Go ahead with analysing all of the project stages in line with the costs displayed on the payment schedule so that you can inform the project manager of the discrepancies.',
        '直接逐一核對所有階段與付款表的金額，完成後再通知項目經理出入。',
        '直接逐一核对所有阶段与付款表的金额，完成后再通知项目经理出入。'
      )
    ],
    [50, 80, 90, 40]
  ),
  item(
    L(
      'The manager of another department has asked you to undertake some work, that is the responsibility of their team, for a fourth time. You were happy to support on the first occasion, but since then you feel you are spending more of your time doing work that is not your responsibility. It seems likely that the manager may be taking advantage of your willingness to help. Whilst you appreciate all departments are currently under-resourced, you are also behind on your deliverables for the week. What do you do?',
      '另一部門的經理已是第四次要求你做本應由他們團隊負責的工作。第一次你樂意幫忙，但之後你覺得自己花越來越多時間做不屬於你職責的事，那位經理似乎在利用你願意幫忙的態度。雖然你理解各部門都資源緊張，但你自己本週的交付也已落後。你會怎樣做？',
      '另一部门的经理已是第四次要求你做本应由他们团队负责的工作。第一次你乐意帮忙，但之后你觉得自己花越来越多时间做不属于你职责的事，那位经理似乎在利用你愿意帮忙的态度。虽然你理解各部门都资源紧张，但你自己本周的交付也已落后。你会怎么做？'
    ),
    [
      L(
        'Set up a meeting with the manager and openly air your concerns. Explain that you have been more than helpful, but that this cannot continue given your responsibility to your work and team, and that you are now behind on your current workload.',
        '約該經理開會坦誠表達憂慮，說明你已多次協助，但基於你對自己工作與團隊的責任，這不能再持續，且你目前工作已落後。',
        '约该经理开会坦诚表达忧虑，说明你已多次协助，但基于你对自己工作与团队的责任，这不能再持续，且你目前工作已落后。'
      ),
      L(
        'Email the manager and be assertive, making it clear that it is unfair that they are asking you for your support for a fourth time. Refuse to do the work this time, as you have been more than helpful many times already.',
        '用電郵強硬表態，指出第四次要求你支援並不公平，並拒絕今次工作，因為你之前已多次幫忙。',
        '用邮件强硬表态，指出第四次要求你支援并不公平，并拒绝这次工作，因为你之前已多次帮忙。'
      ),
      L(
        'Speak to your manager about this and gauge their opinion on what to do. Explain your frustration and tell them that you are unable to support them due to your own deliverables.',
        '先與自己上司商量應如何處理，說明你的困擾，並表示因為自身交付，你無法再支援對方。',
        '先与自己上司商量应如何处理，说明你的困扰，并表示因为自身交付，你无法再支援对方。'
      ),
      L(
        'Tactfully decline the request explaining that you have urgent deliverables which you are behind on. You do not give in to any pressure and stand your ground by politely repeating that you have no time to assist them on this occasion. Make your manager aware of the situation and your chosen course of action.',
        '委婉拒絕，解釋你有緊急交付且已落後；不被施壓影響，禮貌但堅定重申今次無法協助，並向上司交代情況及你的做法。',
        '委婉拒绝，解释你有紧急交付且已落后；不被施压影响，礼貌但坚定重申这次无法协助，并向上司交代情况及你的做法。'
      )
    ],
    [70, 20, 80, 40]
  ),
  item(
    L(
      'You are working in a virtual team on a project that involves individuals based in 4 different locations. The kick-off meeting works well and you find that you are taking the lead on the project. Work is allocated, and weekly sessions to ‘touch-base’ are established. During the following week, one of your colleagues is unable to join the call but does not provide any notice or reason as to why. Later that day, you decide to contact the team member who apologises and explains that they have been sick. Unfortunately, the following week the same occurrence takes place and the rest of the team are unhappy and wish to escalate the matter to management. What do you do?',
      '你在一個跨四地的遠程團隊中工作。啟動會議進行順利，你也自然成為項目帶領者。工作已分配，並建立每週例會作簡短同步。下一週其中一位同事沒有加入通話，也沒有任何通知或原因。你當天聯絡他，他道歉並解釋生病了。不幸地，再下一週同樣情況又發生，其他成員不滿並希望向管理層升級。你會怎樣做？',
      '你在一个跨四地的远程团队中工作。启动会议进行顺利，你也自然成为项目带领者。工作已分配，并建立每周例会作简短同步。下一周其中一位同事没有加入通话，也没有任何通知或原因。你当天联系他，他道歉并解释生病了。不幸地，再下一周同样情况又发生，其他成员不满并希望向管理层升级。你会怎么做？'
    ),
    [
      L(
        'Your team has been fair and tolerant, and if they now wish to escalate the matter, you support them in that; after all, the project is now falling behind.',
        '團隊已相當包容；若大家希望升級處理，你支持他們，畢竟項目已開始落後。',
        '团队已相当包容；若大家希望升级处理，你支持他们，毕竟项目已开始落后。'
      ),
      L(
        'Ask your colleagues to hold off until you contact the colleague and see what is going on. It may be that the individual is undergoing something personal that may explain this behaviour. Based on this, decide whether to escalate the matter.',
        '請大家先暫緩升級，讓你先聯絡該同事了解情況；可能他正面對個人問題。視乎了解結果再決定是否升級。',
        '请大家先暂缓升级，让你先联系该同事了解情况；可能他正面临个人问题。视乎了解结果再决定是否升级。'
      ),
      L(
        'Decide to find someone else to support the project, as your colleague should be considerate enough to do the work for which everyone is accountable. Their actions will make everyone else underperform on their objectives.',
        '直接找其他人替補，因為每人都要為分工負責；他的行為會拖累整個團隊達成目標。',
        '直接找其他人替补，因为每人都要为分工负责；他的行为会拖累整个团队达成目标。'
      ),
      L(
        'Email your colleague and ask him if you can support them with their work. Ask them why they did not produce their share of the work today.',
        '電郵該同事，詢問是否需要你支援他完成工作，並問他今天為何沒有交付自己的部分。',
        '邮件该同事，询问是否需要你支援他完成工作，并问他今天为何没有交付自己的部分。'
      )
    ],
    [70, 90, 30, 80]
  )
];

const passages = COMM_ITEMS.map((row, index) => {
  const n = index + 1;
  return {
    id: `comm-p${n}`,
    title: L(`Scenario ${n}`, `情境 ${n}`, `情境 ${n}`),
    body: row.scenario
  };
});

const questions = COMM_ITEMS.map((row, index) => {
  const n = index + 1;
  const [a, b, c, d] = row.scores;
  return {
    id: `comm-q${n}`,
    displayNumber: n,
    passageId: `comm-p${n}`,
    prompt: PROMPT,
    options: [
      { key: 'A', label: row.options[0] },
      { key: 'B', label: row.options[1] },
      { key: 'C', label: row.options[2] },
      { key: 'D', label: row.options[3] }
    ],
    scoreTable: { A: a, B: b, C: c, D: d }
  };
});

export const communicationTestPaper = {
  slug: 'communication',
  title: L('Communication', '溝通能力', '沟通能力'),
  paperInstruction: L(
    'Read each scenario and choose the response that best matches how you would act. Options are scored on teamwork and collaboration; higher scores reflect more collaborative approaches.',
    '請閱讀每個情境，選擇最符合你會如何行動的回應。各選項按團隊合作與協作程度評分；分數越高代表越具協作性。',
    '请阅读每个情境，选择最符合你会如何行动的回应。各选项按团队合作与协作程度评分；分数越高代表越具协作性。'
  ),
  timeLimitSeconds: 3600,
  order: 3,
  isPublished: true,
  sections: [
    {
      id: 'comm-scenarios',
      title: L(
        'Working with others',
        '情境：協作應對',
        '情境：协作应对'
      ),
      passages,
      questions
    }
  ]
};

